import { useCallback, useEffect, useState } from 'react';
import { money } from '../lib/money';
import { ApiFailure, api } from '../lib/api';
import { Settlements } from './Settlements';

interface Item {
  id: string;
  orderNo: string;
  receiverName: string;
  receiverAddress: string;
  packageCount: number;
  payout: string;
  currency: string;
  deliveredAt: string;
  /** 「我为什么拿这个数」—— 司机视角的推导,不是客户那份明细 */
  lines: Array<{ code: string; label: string; amount: number }>;
  note: string;
  /** 这一单进没进结算单;进了是哪一张、什么状态 */
  settlement: { id: string; status: 'DRAFT' | 'CONFIRMED' | 'PAID' } | null;
}

interface Earnings {
  period: 'today' | 'yesterday' | 'week' | 'lastWeek';
  from: string;
  currency: string;
  deliveredCount: number;
  failedCount: number;
  returnedCount: number;
  payout: string;
  payoutEnabled: boolean;
  items: Item[];
}

/**
 * 收入。
 *
 * ★ 两个原则:
 *
 *   1. **计价没启用时不显示 0 元**。计价引擎接上之前 payout 全是 0。
 *      在收入页上大大地写「MYR 0.00」比什么都不写更伤 ——
 *      司机会以为自己白干了,或者以为系统在克扣。
 *      这时改为只展示单量,并说明金额以线下结算为准。
 *   2. **给明细,不只给总数**。「今天 12 单」司机没法核对;
 *      列出每一单送到哪、几点送的,他才能拿去对账。
 *      钱的数字必须可验算,否则出一次错就再也没有信任度。
 *   3. **每一单要能展开看「为什么是这个数」**。原来后台一行行看得到,
 *      司机自己看不到 —— 而对这个数有疑问的正是他,
 *      他唯一的办法是去问调度,调度再去后台查一遍。
 *
 * ★ 展开的明细是**司机视角**的:配送费 + 偏远加价。
 *   客户侧的构成(起步价、续重、代收手续费、税)一项都不给 ——
 *   那是公司和客户之间的账,摊开在司机面前就变成一次谈判。
 */
export function Earnings() {
  /*
   * ★ 结算单放在「收入」里,而不是底部再加一个标签。
   *
   *   一是手机底栏四个已经够挤;二是司机找「这个月发我多少」时,
   *   第一个点开的就是「收入」—— 让他在这里找不到、再去别处找,
   *   本身就是设计没做对。
   */
  const [period, setPeriod] = useState<
    'today' | 'yesterday' | 'week' | 'lastWeek' | 'settlements'
  >('today');
  const [data, setData] = useState<Earnings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (period === 'settlements') return;
    setLoading(true);
    try {
      setData(await api<Earnings>(`/driver/earnings?period=${period}`));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="pb-24">
      <div className="px-4 pt-4">
        {/*
          ★ 「昨天」和「上周」必须在。
            司机报的:周日跑完的单,周一打开**哪儿都找不到** ——
            「今天」里没有,「本周」从周一算起也没有。
            他昨天挣的钱在这个界面上不存在。

          ★ 五个按钮一行在手机上太挤,换成能横滑的一条。
        */}
        <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
          {(
            [
              ['today', '今天'],
              ['yesterday', '昨天'],
              ['week', '本周'],
              ['lastWeek', '上周'],
              ['settlements', '结算单'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setPeriod(k)}
              className={`h-11 shrink-0 rounded-xl px-4 text-base font-medium transition ${
                period === k
                  ? 'bg-brand-600 text-white'
                  : 'border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {period === 'settlements' && <Settlements />}

        {period !== 'settlements' && error && (
          <div className="mb-3 rounded-xl bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-4 py-3 text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {period !== 'settlements' && !data && !error && <p className="text-slate-400">加载中…</p>}

        {period !== 'settlements' && data && (
          <>
            <div className="rounded-2xl bg-white dark:bg-slate-800 p-5 border
                            border-slate-200 dark:border-slate-700">
              {data.payoutEnabled ? (
                <>
                  <div className="text-sm text-slate-500">
                    {period === 'today' ? '今日收入' : '本周收入'}
                  </div>
                  <div className="mt-1 text-4xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                    {money(data.payout, data.currency)}
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    完成 {data.deliveredCount} 单
                  </div>
                </>
              ) : (
                <>
                  {/* ★ 计价未启用:显示单量,不显示 0 元 */}
                  <div className="text-sm text-slate-500">
                    {period === 'today' ? '今日完成' : '本周完成'}
                  </div>
                  <div className="mt-1 text-4xl font-bold text-slate-900 dark:text-slate-100 tabular-nums">
                    {data.deliveredCount} <span className="text-lg font-normal">单</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">
                    本店尚未启用按单计价,金额以线下结算为准
                  </p>
                </>
              )}

              {(data.failedCount > 0 || data.returnedCount > 0) && (
                <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-700
                                flex gap-4 text-sm">
                  {data.failedCount > 0 && (
                    <span className="text-red-600 dark:text-red-400">
                      未送达 {data.failedCount} 单
                    </span>
                  )}
                  {data.returnedCount > 0 && (
                    <span className="text-orange-600 dark:text-orange-400">
                      退回 {data.returnedCount} 单
                    </span>
                  )}
                </div>
              )}
            </div>

            <h2 className="mt-5 mb-2 px-1 text-sm font-medium text-slate-600 dark:text-slate-400">
              完成明细({data.items.length})
            </h2>

            {data.items.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300
                              dark:border-slate-700 p-8 text-center text-slate-400">
                {
                  {
                    today: '今天还没有完成的单',
                    yesterday: '昨天没有完成的单',
                    week: '本周还没有完成的单',
                    lastWeek: '上周没有完成的单',
                  }[period as 'today' | 'yesterday' | 'week' | 'lastWeek']
                }
              </div>
            ) : (
              <ul className="space-y-1.5">
                {data.items.map((it) => (
                  <li
                    key={it.id}
                    className="rounded-xl border border-slate-200 dark:border-slate-800
                               bg-white dark:bg-slate-800/50 px-3 py-2.5"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-slate-900 dark:text-slate-100">
                        {it.receiverName}
                      </span>
                      {/* 单笔金额同样只在计价启用时显示 */}
                      {data.payoutEnabled && (
                        <span className="shrink-0 tabular-nums font-medium text-slate-900 dark:text-slate-100">
                          {money(it.payout, it.currency)}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-slate-500">
                      {it.receiverAddress}
                    </div>
                    {data.payoutEnabled && it.lines?.length > 0 && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-xs text-brand-600 dark:text-brand-400 list-none">
                          怎么算的
                        </summary>
                        <div className="mt-1 rounded-lg bg-slate-50 dark:bg-slate-900/50 px-2.5 py-2 text-xs">
                          {it.lines.map((l, i) => (
                            <div key={`${l.code}-${i}`} className="flex justify-between py-0.5">
                              <span className="text-slate-500">{l.label}</span>
                              <span className="tabular-nums text-slate-700 dark:text-slate-300">
                                {money(String(l.amount), it.currency)}
                              </span>
                            </div>
                          ))}
                          <div className="mt-1 border-t border-slate-200 dark:border-slate-700 pt-1
                                          text-slate-400">
                            {it.note}
                          </div>
                        </div>
                      </details>
                    )}
                    {/*
                      ★ 「结算了没」要**逐单**说,而且分三种。
                        司机的原话是「也不知道结算了没」—— 一个总数
                        回答不了它:同一批单里有些进了已确认的结算单、
                        有些还挂着,而他问的是「上周日那两单算进去了吗」。

                        「在算了(草稿)」这一档最要紧:金额还可能动,
                        对不上就该现在说,而不是等钱到账之后。
                    */}
                    <SettlementTag s={it.settlement} />
                    <div className="mt-0.5 flex gap-2 text-xs text-slate-400">
                      <span className="tabular-nums">
                        {new Date(it.deliveredAt).toLocaleTimeString('zh-CN', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <span>·</span>
                      <span className="font-mono">{it.orderNo}</span>
                      {it.packageCount > 1 && <span>· {it.packageCount} 件</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <button
              onClick={() => void load()}
              disabled={loading}
              className="mt-4 w-full h-11 rounded-xl border border-slate-300 dark:border-slate-600
                         text-slate-600 dark:text-slate-400 disabled:opacity-50"
            >
              {loading ? '刷新中…' : '刷新'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/** 金额是最小单位的整数字符串,显示时才除 100 —— 全程不碰浮点 */

/** 一单的结算状态 */
function SettlementTag({
  s,
}: {
  s: { id: string; status: 'DRAFT' | 'CONFIRMED' | 'PAID' } | null;
}) {
  if (!s) {
    return (
      <div className="mt-1 text-xs text-slate-400">还没进结算单 —— 到结算周期才会算进去</div>
    );
  }
  const label = { DRAFT: '在算了(还没确认)', CONFIRMED: '已确认,待打款', PAID: '已打款' }[
    s.status
  ];
  const tone =
    s.status === 'PAID'
      ? 'text-green-600 dark:text-green-400'
      : s.status === 'CONFIRMED'
        ? 'text-brand-600 dark:text-brand-400'
        : 'text-amber-600 dark:text-amber-400';
  return <div className={`mt-1 text-xs ${tone}`}>{label}</div>;
}
